<template>
  <div class="py-12 px-24 body-font">
    <modal :adaptive="true" :width="420" height="auto" name="sessionPreview" @before-close="closeModal">
      <div class="py-4 px-8">
        <h3 class="text-center font-bold mb-6 text-lg">{{ $t("session.preview.title") }}</h3>
        <div class="relative video-preview">
          <div
            v-show="mediaPermissionsChecked && (!isVideoOn || !mediaPermissionsGranted)"
            class="video-bg bg-inactiveGraphics video-overlay global-border-radius"
          >
            {{ $t("video.disabled") }}
          </div>
          <video ref="testVideo" autoplay class="video-preview__media object-cover global-border-radius" muted></video>

          <div class="controls flex">
            <div class="py-4 px-6 cursor-pointer" @click="toggleAudio">
              <img v-if="isAudioOn" alt="on" class="h-8 w-8" src="../assets/images/video/micro-on.svg" />
              <img v-else alt="on" class="h-8 w-8" src="../assets/images/video/micro-off.svg" />
            </div>
            <div class="py-4 px-6 cursor-pointer" @click="toggleVideo">
              <img v-if="isVideoOn" alt="on" class="h-8 w-8" src="../assets/images/video/video-on.svg" />
              <img v-else alt="on" class="h-8 w-8" src="../assets/images/video/video-off.svg" />
            </div>
          </div>
        </div>
        <div class="flex justify-between items-center mt-6">
          <secondary-button @click="$modal.hide('sessionPreview')">
            {{ $t("cancel.generic") }}
          </secondary-button>

          <primary-button :disabled="!mediaPermissionsGranted" @click="joinSession">
            {{ $t("video.join") }}
          </primary-button>
        </div>
      </div>
    </modal>
    <modal :adaptive="true" :width="420" height="auto" name="cancelSession">
      <div class="py-4 px-8">
        <h3 class="text-center font-bold mb-6 text-lg main-font">
          {{ $t("session.cancel.title") }}
        </h3>

        <div class="relative">
          <h4 class="font-semibold text-13 mb-2">{{ $t("session.cancel.subtitle") }}</h4>
          <div class="card-border-radius neutral-background-4 shadow-lg cursor-pointer text-sm">
            <p
              v-for="(r, index) in reasons"
              :key="r"
              :class="{
                'primary-background-active text-white': index === reason,
                'card-border-t-radius': index === 0,
                'card-border-b-radius': index === reasons.length - 1
              }"
              class="py-2 px-4 cursor-pointer border-b border-gray-300"
              @click="choseReason(index)"
            >
              {{ r }}
            </p>
          </div>
        </div>

        <div v-if="reason === 5" class="input-wrapper flex justify-start items-center mt-8 mb-4">
          <textarea v-model="comment" :placeholder="$t('session.cancel.reason')" type="text"></textarea>
        </div>

        <div class="flex justify-between items-center mt-6">
          <secondary-button @click="$modal.hide('cancelSession')">
            {{ $t("back.generic") }}
          </secondary-button>
          <primary-button :disabled="reason === -1 || (reason === 5 && comment.length < 3)" @click="cancelAppointment">
            {{ $t("session.cancel.button") }}
          </primary-button>
        </div>
      </div>
    </modal>

    <h2 class="text-xl font-semibold main-font">{{ $t("sessions.title") }}</h2>
    <div class="flex justify-start text-sm mt-4 mb-8">
      <p :class="{ 'interactive-text': accepted }" class="mr-4 cursor-pointer" @click="accepted = true">
        {{ $t("sessions.accepted") }}
      </p>
      <p :class="{ 'interactive-text': !accepted }" class="cursor-pointer" @click="accepted = false">
        {{ $t("sessions.cancelled") }}
      </p>
    </div>
    <!-- ACCEPTED -->
    <div v-if="accepted" class="table-wrapper">
      <div v-if="acceptedAppointments.length > 0" class="table-wrapper_content">
        <!-- HEADER -->
        <div class="header sessions-grid font-semibold px-6 mb-4">
          <div v-if="showClientName" class="sessions-item text-left justify-start col-span-2">
            {{ $t("sessions.client.name") }}
          </div>
          <div class="sessions-item text-left justify-start col-span-1">
            {{ $t("sessions.date") }}
          </div>
          <div class="sessions-item text-left justify-start col-span-1">
            {{ $t("sessions.time") }}
          </div>
          <div v-if="showPhone" class="sessions-item text-left justify-start col-span-2">
            {{ $t("sessions.client.phone") }}
          </div>
          <div v-if="showClientEmail" class="sessions-item text-left justify-start col-span-2">
            {{ $t("sessions.email") }}
          </div>
          <div v-if="showLanguagePreferences" class="sessions-item text-left justify-start col-span-1">
            {{ $t("sessions.language") }}
          </div>
          <div v-if="showVoucher" class="sessions-item text-left justify-start col-span-1">
            {{ $t("sessions.voucher") }}
          </div>
          <div v-if="showStatus" class="sessions-item text-left justify-start col-span-1">
            {{ $t("sessions.status") }}
          </div>
          <div v-if="showPaymentStatus" class="sessions-item text-left justify-start col-span-2">
            {{ $t("sessions.payment_status") }}
          </div>
          <div class="sessions-item text-left justify-start col-span-3" />
        </div>
        <!-- ROWS -->
        <div class="card-border-radius">
          <div v-for="(a, index) in acceptedAppointments" :key="a.id">
            <!-- ROW -->
            <div
              class="content sessions-grid text-sm py-3 px-6 neutral-background-4"
              :class="{
                'card-border-radius': acceptedAppointments.length === 1,
                'card-border-t-radius': acceptedAppointments.length > 1 && index === 0,
                'card-border-b-radius': acceptedAppointments.length > 1 && index !== 0
              }"
            >
              <div v-if="showClientName" class="sessions-item text-left justify-start font-semibold col-span-2">
                {{ a.client_full_name }}
              </div>
              <div class="sessions-item text-left justify-start col-span-1">
                {{ getDate(a) }}
              </div>
              <div class="sessions-item text-left justify-start col-span-1">
                {{ getTime(a) }}
              </div>
              <div v-if="showPhone" class="sessions-item text-left justify-start break-all col-span-2">
                {{ a.client.phone }}
              </div>
              <div
                v-if="showClientEmail"
                class="sessions-item text-left justify-start font-semibold break-all col-span-2"
              >
                {{ a.client_email }}
              </div>
              <div v-if="showLanguagePreferences" class="sessions-item text-left justify-start col-span-1">
                {{ a.coaching_language }}
              </div>
              <div v-if="showVoucher" class="sessions-item text-left justify-start col-span-1">
                <template v-if="a.voucher">
                  <span class="voucher-text" :title="a.voucher">{{ a.voucher }}</span>
                  <button type="button" class="copy-btn" @click.stop="copyVoucher(a.voucher)">
                    <img alt="copy" class="h-4 w-4" src="../assets/images/copy-btn.svg" />
                  </button>
                </template>
                <template v-else>
                  <span class="voucher-text">--------</span>
                </template>
              </div>
              <div v-if="showStatus" class="sessions-item text-left justify-start col-span-1">
                <span :class="['status-pill', getStatusClass(a.status)]">
                  {{ getStatusLabel(a.status) }}
                </span>
              </div>
              <div v-if="showPaymentStatus" class="sessions-item text-left justify-start col-span-2">
                <select-dropdown
                  :value="a.payment_status"
                  :options="paymentStatusOptions"
                  :disabled="updatingPaymentStatusId === a.id"
                  :placeholder="$t('sessions.payment_status')"
                  @change="onPaymentStatusChange(a, $event)"
                />
              </div>
              <div class="sessions-item text-left justify-end col-span-3">
                <div class="flex items-center justify-end space-x-3">
                  <template v-if="expandedIndex !== index">
                    <span
                      class="interactive-text show-more-text cursor-pointer font-semibold whitespace-no-wrap"
                      @click="expandedIndex = index"
                    >
                      {{ $t("sessions.info.show") }}
                    </span>
                  </template>
                  <template v-else>
                    <span
                      class="interactive-text show-more-text cursor-pointer font-semibold whitespace-no-wrap"
                      @click="expandedIndex = -1"
                    >
                      {{ $t("sessions.info.hide") }}
                    </span>
                  </template>
                  <primary-button @click="openVideoSession(a.id)">
                    {{ $t("join.generic") }}
                  </primary-button>
                </div>
              </div>
            </div>
            <!-- EXPANDED ROW -->
            <div
              v-if="expandedIndex === index"
              class="w-full bg-white border-t neutral-background-4 rounded-b-lg px-6 py-4 flex"
            >
              <div class="w-1/2">
                <h3 class="font-semibold mb-2">{{ $t("selected.areas") }}</h3>
                <div class="flex flex-row justify-start flex-wrap">
                  <div
                    v-for="area in a.areas"
                    :key="area.id + 'area'"
                    class="mr-2 mb-2 tag-border-radius px-5 py-2 neutral-background-4 border-2 border-primary"
                  >
                    {{ area.name }}
                  </div>
                </div>
              </div>

              <div class="w-1/2">
                <h3 class="font-semibold mb-2" v-if="showComment">
                  {{ $t("comment.generic") }}
                </h3>
                <p v-if="showComment">{{ a.comment }}</p>
                <div class="flex justify-end">
                  <primary-button class="mt-5" @click="openCancelAppointment(a.id)">
                    {{ $t("cancel.generic") }}
                  </primary-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="w-full flex justify-center items-center flex-col mt-12">
        <img alt="no-sessions" class="w-16 h-16" src="../assets/images/calendar-empty-icon.svg" />
        <h2 class="secondary-text mt-3">
          {{ $t("sessions.no.accepted") }}
        </h2>
      </div>
    </div>

    <!-- CANCELED -->
    <div v-else class="table-wrapper">
      <div v-if="canceledAppointments.length > 0" class="table-wrapper_content">
        <div class="header sessions-grid font-semibold px-6 mb-4">
          <div v-if="showClientName" class="sessions-item text-left justify-start col-span-2">
            {{ $t("sessions.client.name") }}
          </div>
          <div v-if="showClientEmail" class="sessions-item text-left justify-start col-span-2">
            {{ $t("sessions.client.email") }}
          </div>
          <div class="sessions-item text-left justify-start col-span-1">
            {{ $t("sessions.date") }}
          </div>
          <div class="sessions-item text-left justify-start col-span-1">
            {{ $t("sessions.time") }}
          </div>
          <div class="sessions-item text-left justify-start col-span-2">
            {{ $t("sessions.who.cancel") }}
          </div>
          <div v-if="showPaymentStatus" class="sessions-item text-left justify-start col-span-2">
            {{ $t("sessions.payment_status") }}
          </div>
          <div class="sessions-item text-left justify-start col-span-2">
            {{ $t("sessions.cancellation.date") }}
          </div>
        </div>
        <div class="card-border-radius overflow-hidden">
          <div v-for="a in canceledAppointments" :key="a.id">
            <div class="content sessions-grid text-sm neutral-background-4 px-6 py-3">
              <div v-if="showClientName" class="sessions-item text-left justify-start font-semibold col-span-2">
                {{ a.client_full_name }}
              </div>
              <div
                v-if="showClientEmail"
                class="sessions-item text-left justify-start font-semibold break-all col-span-2"
              >
                {{ a.client_email }}
              </div>
              <div class="sessions-item text-left justify-start col-span-1">
                {{ getDate(a) }}
              </div>
              <div class="sessions-item text-left justify-start col-span-1">
                {{ getTime(a) }}
              </div>
              <div class="sessions-item text-left justify-start col-span-2">
                {{ getCancelledBy(a.status) }}
              </div>
              <div v-if="showPaymentStatus" class="sessions-item text-left justify-start col-span-2">
                {{ getPaymentStatusLabel(a.payment_status) }}
              </div>
              <div class="sessions-item text-left justify-start col-span-2">
                {{ getCancellationDate(a) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="w-full flex justify-center items-center flex-col mt-12">
        <img alt="no-sessions" class="w-16 h-16" src="../assets/images/calendar-empty-icon.svg" />
        <h2 class="secondary-text mt-3">
          {{ $t("sessions.no.canceled") }}
        </h2>
      </div>
    </div>
  </div>
</template>

<script>
import PrimaryButton from "@/components/ui-kit/buttons/PrimaryButton";
import { mapState } from "vuex";
import dayjs from "dayjs";
import SecondaryButton from "@/components/ui-kit/buttons/SecondaryButton";
import SelectDropdown from "@/components/ui-kit/form/SelectDropdown";
import { PAYMENT_STATUSES, SESSION_STATUSES } from "@/constants";

export default {
  name: "Sessions",
  components: { SecondaryButton, PrimaryButton, SelectDropdown },
  data() {
    return {
      accepted: true,
      expandedIndex: -1,
      isAudioOn: true,
      isVideoOn: true,
      selectedId: null,
      mediaPermissionsGranted: false,
      mediaPermissionsChecked: false,
      reason: -1,
      reasons: [
        this.$t("reasons.sickness"),
        this.$t("reasons.technical_issues"),
        this.$t("reasons.conflicting_appointments"),
        this.$t("reasons.no_show"),
        this.$t("reasons.not_matching_requirements"),
        this.$t("reasons.other")
      ],
      reasonOpened: false,
      comment: "",
      showClientEmail: process.env.VUE_APP_SHOW_CLIENT_EMAIL === "true",
      showComment: process.env.VUE_APP_SHOW_COMMENT === "true",
      showClientName: process.env.VUE_APP_SHOW_CLIENT_NAME === "true",
      showVoucher: process.env.VUE_APP_SHOW_VOUCHER === "true",
      showStatus: process.env.VUE_APP_SHOW_STATUS === "true",
      showPaymentStatus: process.env.VUE_APP_SHOW_PAYMENT_STATUS === "true",
      showLanguagePreferences: process.env.VUE_APP_SHOW_LANGUAGE_PREFERENCES === "true",
      showPhone: process.env.VUE_APP_SHOW_CLIENT_PHONE === "true",
      updatingPaymentStatusId: null,
      PAYMENT_STATUSES
    };
  },

  computed: {
    ...mapState("sessions", ["appointments", "acceptedAppointments", "canceledAppointments", "videoRoom"]),
    paymentStatusOptions() {
      return [
        {
          value: PAYMENT_STATUSES.NOT_VALIDATED,
          label: this.$t("payment_status.not_validated")
        },
        {
          value: PAYMENT_STATUSES.PAID_VIA_LINK,
          label: this.$t("payment_status.paid_via_link")
        },
        {
          value: PAYMENT_STATUSES.PAID_VIA_VOUCHER,
          label: this.$t("payment_status.paid_via_voucher")
        },
        {
          value: PAYMENT_STATUSES.NOT_PAID,
          label: this.$t("payment_status.not_paid")
        }
      ];
    }
  },

  methods: {
    getDate(a) {
      if (!a) {
        return " - ";
      }
      return dayjs(a.start).format("DD.MM.YY");
    },

    getCancellationDate(a) {
      if (!a) {
        return " - ";
      }
      return dayjs(a.canceled_at).format("DD-MM-YYYY HH:mm");
    },
    getTime(a) {
      if (!a.start) {
        return " - ";
      }
      return dayjs(a.start).format("HH:mm");
    },

    showSession(start) {
      return dayjs(start) > dayjs();
    },

    openCancelAppointment(id) {
      this.selectedId = id;
      this.$modal.show("cancelSession");
    },

    async cancelAppointment() {
      this.$modal.hide("cancelSession");
      const reason = this.reason === 5 ? this.comment : this.reasons[this.reason];
      await this.$store.dispatch("sessions/cancelAppointment", { id: this.selectedId, reason });
    },

    async openVideoSession(id) {
      this.selectedId = id;

      this.$modal.show("sessionPreview");
      setTimeout(async () => {
        await this.createTestVideo();
      }, 1000);
    },

    async createTestVideo() {
      const video = this.$refs.testVideo;

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.mediaPermissionsGranted = false;
        this.mediaPermissionsChecked = true;
        this.notifyPermissionDenied();
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (video) {
          video.srcObject = stream;
        }
        this.mediaPermissionsGranted = true;
      } catch (e) {
        console.error("Something went wrong!", e);
        this.mediaPermissionsGranted = false;
        this.notifyPermissionDenied();
      } finally {
        this.mediaPermissionsChecked = true;
      }
    },

    notifyPermissionDenied() {
      this.$notify({
        group: "notifications",
        title: this.$t("session.preview.permission.denied.title"),
        text: this.$t("session.preview.permission.denied.text"),
        type: "error"
      });
    },

    async toggleVideo() {
      const stream = this.$refs.testVideo && this.$refs.testVideo.srcObject;
      if (!stream) return;

      if (this.isVideoOn) {
        stream.getVideoTracks().forEach((t) => {
          t.stop();
          stream.removeTrack(t);
        });
        this.isVideoOn = false;
      } else {
        try {
          const newStream = await navigator.mediaDevices.getUserMedia({ video: true });
          newStream.getVideoTracks().forEach((t) => stream.addTrack(t));
          this.isVideoOn = true;
        } catch (e) {
          console.error(e);
        }
      }
    },

    async toggleAudio() {
      const stream = this.$refs.testVideo && this.$refs.testVideo.srcObject;
      if (!stream) return;

      if (this.isAudioOn) {
        stream.getAudioTracks().forEach((t) => {
          t.stop();
          stream.removeTrack(t);
        });
        this.isAudioOn = false;
      } else {
        try {
          const newStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          newStream.getAudioTracks().forEach((t) => stream.addTrack(t));
          this.isAudioOn = true;
        } catch (e) {
          console.error(e);
        }
      }
    },

    removeTestVideo() {
      if (!this.$refs.testVideo || !this.$refs.testVideo.srcObject) {
        return;
      }
      const stream = this.$refs.testVideo.srcObject;
      const tracks = stream.getTracks();

      tracks.forEach((track) => {
        track.stop();
      });

      this.$refs.testVideo.srcObject = null;
    },

    closeModal() {
      this.removeTestVideo();
      this.mediaPermissionsGranted = false;
      this.mediaPermissionsChecked = false;
      this.isAudioOn = true;
      this.isVideoOn = true;
    },

    async joinSession() {
      this.removeTestVideo();
      await this.$router.push({
        name: "VideoSession",
        query: { videoRoomId: this.selectedId, isAudioOn: this.isAudioOn, isVideoOn: this.isVideoOn }
      });
    },

    choseReason(index) {
      this.reason = index;
      this.reasonOpened = false;
    },
    async copyVoucher(voucher) {
      if (!voucher) return;
      try {
        await navigator.clipboard.writeText(voucher);
        this.$notify({
          group: "notifications",
          title: this.$t("voucher.copy.success"),
          type: "success"
        });
      } catch (e) {
        this.$notify({
          group: "notifications",
          title: this.$t("voucher.copy.error"),
          type: "error"
        });
      }
    },
    getCancelledBy(status) {
      if (status === SESSION_STATUSES.CANCELED_BY_CLIENT) return this.$t("filter.status.3");
      if (status === SESSION_STATUSES.CANCELED_BY_HUMANOO) return this.$t("filter.status.5");
      return this.$t("filter.status.4");
    },
    getStatusLabel(status) {
      if (status === SESSION_STATUSES.ACCEPTED) return "Upcoming";
      if (status === SESSION_STATUSES.COMPLETED) return "Completed";
      return "Canceled";
    },
    getStatusClass(status) {
      if (status === SESSION_STATUSES.ACCEPTED) return "status-upcoming";
      if (status === SESSION_STATUSES.COMPLETED) return "status-completed";
      return "status-canceled";
    },
    getPaymentStatusLabel(status) {
      const option = this.paymentStatusOptions.find((o) => o.value === status);
      return option ? option.label : status || "-";
    },
    async onPaymentStatusChange(appointment, paymentStatus) {
      if (!this.showPaymentStatus) return;
      if (!appointment || appointment.payment_status === paymentStatus) return;

      this.updatingPaymentStatusId = appointment.id;

      try {
        await this.$store.dispatch("sessions/updatePaymentStatus", {
          id: appointment.id,
          payment_status: paymentStatus
        });
      } finally {
        this.updatingPaymentStatusId = null;
      }
    }
  },

  async created() {
    await this.$store.dispatch("sessions/getAppointments");
  }
};
</script>

<style scoped>
.show-more-text {
  font-size: 14px;
}
.table-wrapper {
  overflow: auto;
  padding-bottom: 200px;
}
.table-wrapper_content {
  display: inline-block;
  min-width: calc(100vw - 192px);
}
.sessions-grid {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(80px, 1fr);
  gap: 10px;
  min-width: calc(100vw - 192px);
}
.header .sessions-item {
  font-size: 16px;
}
.content .sessions-item {
  font-size: 14px;
}
.sessions-item {
  @apply flex items-center;
}

.controls {
  position: absolute;
  bottom: 5px;
  left: 50%;
  transform: translate(-50%);
}

.disclaimer {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: -1;
}

.video-bg {
  @apply w-full h-full flex justify-center items-center text-white rounded-md absolute;
  border-radius: var(--global-border-radius);
}

.video-preview {
  width: 100%;
  aspect-ratio: 16 / 9;
}

.video-preview__media {
  width: 100%;
  height: 100%;
}

.video-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: video-spinner-spin 0.8s linear infinite;
}

@keyframes video-spinner-spin {
  to {
    transform: rotate(360deg);
  }
}

.copy-btn {
  margin-left: 4px;
  height: 16px;
  width: 16px;
  min-height: 16px;
  min-width: 16px;
  outline: none;
}

/* Voucher text */
.voucher-text {
  display: inline-block;
  max-width: 57px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Status pill */
.status-pill {
  padding: 2px 4px;
  border-radius: 6px;
  font-weight: 400;
  font-size: 10px;
  line-height: 100%;
}

.status-upcoming {
  background: #d4ffdc;
  color: #3e914d;
}

.status-canceled {
  background: #ffc7c5;
  color: #da3832;
}

.status-completed {
  background: #f1f1f1;
  color: #9e9e9e;
}

textarea {
  @apply p-3 rounded-xl border-2 transition-all duration-300;
  color: var(--primary-text);
  border-color: var(--neutral-background-1);
  resize: none;
  background: #fff;
  width: 500px;

  &.active {
    @apply h-40;
  }

  &::placeholder {
    color: var(--secondary-text);
  }

  &:focus {
    border-color: var(--primary-background);
    outline: none;
  }
}
</style>
